import { Sprout, Truck, GraduationCap, Network, Database } from "lucide-react";

const pillars = [
  { num: "01", icon: Sprout, label: "Community Gardens", sub: "Climate-adapted" },
  { num: "02", icon: Truck, label: "Mobile Horticulture", sub: "Specialist support on-Country" },
  { num: "03", icon: GraduationCap, label: "Training Pathways", sub: "Accredited & employment-linked" },
  { num: "04", icon: Database, label: "Seed Systems", sub: "Local & indigenous varieties" },
  { num: "05", icon: Network, label: "Regional Networks", sub: "Connected resilience" },
];

export default function SystemDiagram() {
  return (
    <section
      id="system"
      data-testid="system-diagram-section"
      className="relative bg-charcoal text-sand py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-ochre mb-6">
              The System
            </div>
            <h2
              data-testid="system-heading"
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-sand"
            >
              The Growing Country
              <span className="block italic text-ochre">system.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-sand/80">
              Five interconnected components — designed as resilience
              infrastructure, scalable across regions, and Indigenous-led
              from the ground up.
            </p>
          </div>
        </div>

        {/* Diagram */}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Hub-and-spoke SVG (desktop) */}
          <div className="hidden lg:block lg:col-span-7">
            <div className="relative w-full aspect-square max-w-[640px]">
              <SystemSVG />
            </div>
          </div>

          {/* Mobile/right column legend */}
          <div className="lg:col-span-5 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.28em] text-ochre/80 mb-6">
              System Components
            </div>
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.num}
                  data-testid={`system-component-${i}`}
                  className="group flex items-start gap-5 border-t border-sand/15 pt-5 hover:border-ochre/60 transition-colors"
                >
                  <div className="font-serif text-3xl text-ochre tick-num leading-none w-12 shrink-0">
                    {p.num}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <Icon strokeWidth={1.5} className="w-5 h-5 text-sand/80 group-hover:text-ochre transition-colors" />
                      <h3 className="font-serif text-2xl text-sand">
                        {p.label}
                      </h3>
                    </div>
                    <p className="text-sm text-sand/60 ml-8">{p.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom footer */}
        <div className="mt-20 md:mt-28 pt-12 border-t border-sand/15 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {[
            { k: "Structure", v: "National framework, regionally implemented" },
            { k: "Ownership", v: "Community-owned, community-led" },
            { k: "Scale", v: "Modular & replicable across remote Australia" },
          ].map((s) => (
            <div key={s.k}>
              <div className="text-[11px] uppercase tracking-[0.28em] text-ochre/80 mb-3">
                {s.k}
              </div>
              <div className="font-serif text-xl md:text-2xl text-sand leading-snug">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SystemSVG() {
  const center = { x: 250, y: 250 };
  const r = 170;
  const nodes = [
    { label: "Gardens", short: "01", angle: -90 },
    { label: "Mobile", short: "02", angle: -18 },
    { label: "Training", short: "03", angle: 54 },
    { label: "Seeds", short: "04", angle: 126 },
    { label: "Networks", short: "05", angle: 198 },
  ];
  const pts = nodes.map((n) => {
    const rad = (n.angle * Math.PI) / 180;
    return { ...n, x: center.x + r * Math.cos(rad), y: center.y + r * Math.sin(rad) };
  });

  return (
    <svg viewBox="0 0 500 500" className="w-full h-full" role="img" aria-label="Growing Country system diagram">
      {/* outer ring */}
      <circle cx={center.x} cy={center.y} r={r + 30} fill="none" stroke="#D68C45" strokeOpacity="0.15" strokeDasharray="2 6" />
      <circle cx={center.x} cy={center.y} r={r} fill="none" stroke="#D68C45" strokeOpacity="0.25" />

      {/* spokes */}
      {pts.map((p, i) => (
        <line
          key={i}
          x1={center.x}
          y1={center.y}
          x2={p.x}
          y2={p.y}
          stroke="#D68C45"
          strokeOpacity="0.35"
          strokeWidth="1"
        />
      ))}

      {/* center hub */}
      <circle cx={center.x} cy={center.y} r="58" fill="#BC472A" />
      <text x={center.x} y={center.y - 6} textAnchor="middle" fill="#F8F6F0" fontFamily="Cormorant Garamond, serif" fontSize="20" fontStyle="italic">
        Growing
      </text>
      <text x={center.x} y={center.y + 16} textAnchor="middle" fill="#F8F6F0" fontFamily="Cormorant Garamond, serif" fontSize="20">
        Country
      </text>

      {/* nodes */}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="38" fill="#1C1917" stroke="#D68C45" strokeWidth="1" />
          <text x={p.x} y={p.y - 4} textAnchor="middle" fill="#D68C45" fontFamily="Outfit, sans-serif" fontSize="10" letterSpacing="2">
            {p.short}
          </text>
          <text x={p.x} y={p.y + 14} textAnchor="middle" fill="#F8F6F0" fontFamily="Cormorant Garamond, serif" fontSize="15">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
